/**
 * Site / company access switches for the User Details modal (Related Companies tab).
 *
 * CR-6076 AC3: sites are grouped under their division (CompanyGroup). Each
 * division renders a header with a bulk link/unlink toggle and optional module
 * cascade ("apply to all sites"), with the individual per-site cards still
 * available and editable underneath.
 */

import { useState } from 'react';
import type { ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import {
  Building2, Power, FileCheck, CheckCircle, Eye,
  Loader2, ChevronDown, ChevronRight, Layers, ShieldCheck,
} from 'lucide-react';
import type { UserProfile, UserSitePermission } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import { SiteNotificationSwitches } from './SiteNotificationSwitches';

function Label({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={className}>{children}</div>;
}

export interface UserDetailsSiteAccessTabProps {
  userSitePermissions: UserSitePermission[];
  loadingSitePermissions: boolean;
  savingPermission: number | null;
  savingDivision: number | null;
  selectedUser: UserProfile;
  isSelf: (userProfile: UserProfile) => boolean;
  handleToggleSiteLink: (siteId: number, isLinked: boolean) => Promise<void>;
  handleToggleSitePermission: (siteId: number, permission: string, value: boolean) => Promise<void>;
  handleAssignDivision: (
    groupId: number,
    opts: { isLinked: boolean; modules?: Record<string, boolean>; applyModulesToAll?: boolean }
  ) => Promise<void>;
}

interface DivisionGroup {
  id: number | null;
  name: string;
  rows: UserSitePermission[];
}

interface TopGroup {
  id: number;
  name: string;
  divisions: Map<string, DivisionGroup>;
}

interface SharedSectionHandlers {
  selectedUser: UserProfile;
  isSelf: (userProfile: UserProfile) => boolean;
  savingPermission: number | null;
  savingDivision: number | null;
  handleToggleSiteLink: (siteId: number, isLinked: boolean) => Promise<void>;
  handleToggleSitePermission: (siteId: number, permission: string, value: boolean) => Promise<void>;
  handleAssignDivision: (
    groupId: number,
    opts: { isLinked: boolean; modules?: Record<string, boolean>; applyModulesToAll?: boolean }
  ) => Promise<void>;
}

export function UserDetailsSiteAccessTab({
  userSitePermissions,
  loadingSitePermissions,
  savingPermission,
  savingDivision,
  selectedUser,
  isSelf,
  handleToggleSiteLink,
  handleToggleSitePermission,
  handleAssignDivision,
}: UserDetailsSiteAccessTabProps) {
  // CR-6076 follow-up: the top-group "Link entire group" (cross-division)
  // control is a Helix-admin capability; customer admins manage per-site /
  // division within their own scope only.
  const { user } = useAuth();
  const viewerIsHelix = user?.company_type === 'helix';
  if (loadingSitePermissions) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Loader2 className="h-12 w-12 mx-auto mb-4 opacity-50 animate-spin" />
        <p>Loading company access...</p>
      </div>
    );
  }

  // Drop the synthetic read-only group entry (CR-5311) — the division headers
  // below now make group membership visible with real, actionable toggles.
  const siteRows = userSitePermissions.filter((p) => !p.is_group_entry && p.site != null);

  if (siteRows.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>No company access configured for this user.</p>
      </div>
    );
  }

  // Build the Group -> Division -> Site tree. Sites whose division has a parent
  // group nest under that top group; divisions with no parent (and any
  // ungrouped sites) render at the top level, so a 2-level org still works.
  const topGroups = new Map<string, TopGroup>();
  const looseDivisions = new Map<string, DivisionGroup>();
  for (const row of siteRows) {
    const divId = row.group_id ?? null;
    const divKey = divId === null ? 'ungrouped' : String(divId);
    const parentId = row.parent_group_id ?? null;
    if (parentId !== null && divId !== null) {
      const pKey = String(parentId);
      let tg = topGroups.get(pKey);
      if (!tg) {
        tg = { id: parentId, name: row.parent_group_name || 'Group', divisions: new Map() };
        topGroups.set(pKey, tg);
      }
      let div = tg.divisions.get(divKey);
      if (!div) {
        div = { id: divId, name: row.group_name || 'Division', rows: [] };
        tg.divisions.set(divKey, div);
      }
      div.rows.push(row);
    } else {
      let div = looseDivisions.get(divKey);
      if (!div) {
        div = { id: divId, name: row.group_name || 'Other companies', rows: [] };
        looseDivisions.set(divKey, div);
      }
      div.rows.push(row);
    }
  }

  const shared: SharedSectionHandlers = {
    selectedUser, isSelf, savingPermission, savingDivision,
    handleToggleSiteLink, handleToggleSitePermission, handleAssignDivision,
  };

  return (
    <div className="space-y-4">
      {Array.from(topGroups.values()).map((tg) => (
        <GroupSection key={`g-${tg.id}`} group={tg} viewerIsHelix={viewerIsHelix} {...shared} />
      ))}
      {Array.from(looseDivisions.values()).map((div) => (
        <DivisionSection key={`d-${div.id ?? 'ungrouped'}`} group={div} {...shared} />
      ))}
    </div>
  );
}

interface GroupSectionProps extends SharedSectionHandlers {
  group: TopGroup;
  viewerIsHelix: boolean;
}

/** Top-level group node (CR-6076 AC3) — a "link entire group" toggle that spans
 *  all of its divisions, with each division rendered beneath. The toggle is
 *  shown only to Helix admins; customer admins manage within their own scope. */
function GroupSection({ group, viewerIsHelix, ...handlers }: GroupSectionProps) {
  const [expanded, setExpanded] = useState(true);
  const divisions = Array.from(group.divisions.values());
  const allRows = divisions.flatMap((d) => d.rows);
  const total = allRows.length;
  const linkedCount = allRows.filter((r) => r.is_linked).length;
  const allLinked = total > 0 && linkedCount === total;
  const isSaving = handlers.savingDivision === group.id;
  const disabled = handlers.isSelf(handlers.selectedUser) || isSaving;

  return (
    <Card className={`border-primary/30 ${isSaving ? 'animate-pulse' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center gap-2 text-left"
          >
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <Building2 className="h-4 w-4 text-primary" />
            <CardTitle className="text-base">{group.name}</CardTitle>
            <span className="text-xs text-muted-foreground">
              {linkedCount} of {total} linked · {divisions.length} division{divisions.length === 1 ? '' : 's'}
            </span>
          </button>
          {viewerIsHelix && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{allLinked ? 'All linked' : 'Link entire group'}</span>
              <Switch
                checked={allLinked}
                onCheckedChange={(checked) => handlers.handleAssignDivision(group.id, { isLinked: checked })}
                disabled={disabled}
              />
            </div>
          )}
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-3">
          {divisions.map((div) => (
            <DivisionSection key={`d-${div.id ?? 'ungrouped'}`} group={div} {...handlers} />
          ))}
        </CardContent>
      )}
    </Card>
  );
}

interface DivisionSectionProps extends SharedSectionHandlers {
  group: DivisionGroup;
}

function DivisionSection({
  group, selectedUser, isSelf, savingPermission, savingDivision,
  handleToggleSiteLink, handleToggleSitePermission, handleAssignDivision,
}: DivisionSectionProps) {
  const [expanded, setExpanded] = useState(true);
  // CR-8252: the Compliance permission only exists while the tenant has compliance.
  const complianceOn = useMenuSettings().isMenuItemVisible('onboarding');

  const total = group.rows.length;
  const linkedRows = group.rows.filter((r) => r.is_linked);
  const linkedCount = linkedRows.length;
  const allLinked = total > 0 && linkedCount === total;
  const isSavingDivision = group.id != null && savingDivision === group.id;
  const disabledDivision = isSelf(selectedUser) || isSavingDivision || group.id == null;

  // Cascade-module state: a module is "on for all" when every linked site has it.
  const allHave = (field: 'can_purchasing' | 'can_quality' | 'can_compliance') =>
    linkedCount > 0 && linkedRows.every((r) => r[field]);

  return (
    <Card className={isSavingDivision ? 'animate-pulse' : ''}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center gap-2 text-left"
          >
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <Layers className="h-4 w-4 text-primary" />
            <CardTitle className="text-base">{group.name}</CardTitle>
            <span className="text-xs text-muted-foreground">
              {linkedCount} of {total} linked
            </span>
          </button>
          {group.id != null && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {allLinked ? 'All linked' : 'Link all'}
              </span>
              <Switch
                checked={allLinked}
                onCheckedChange={(checked) => handleAssignDivision(group.id as number, { isLinked: checked })}
                disabled={disabledDivision}
              />
            </div>
          )}
        </div>
        {group.id != null && linkedCount > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-2 pl-6">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Apply to all sites
            </span>
            <ModuleCascade
              label="Purchasing"
              checked={allHave('can_purchasing')}
              disabled={isSelf(selectedUser) || isSavingDivision}
              onChange={(value) =>
                handleAssignDivision(group.id as number, {
                  isLinked: true,
                  modules: { can_purchasing: value },
                  applyModulesToAll: true,
                })
              }
            />
            <ModuleCascade
              label="Quality"
              checked={allHave('can_quality')}
              disabled={isSelf(selectedUser) || isSavingDivision}
              onChange={(value) =>
                handleAssignDivision(group.id as number, {
                  isLinked: true,
                  modules: { can_quality: value },
                  applyModulesToAll: true,
                })
              }
            />
            {/* CR-8252: the per-user level of the compliance gate. */}
            {complianceOn && (
              <ModuleCascade
                label="Compliance"
                checked={allHave('can_compliance')}
                disabled={isSelf(selectedUser) || isSavingDivision}
                onChange={(value) =>
                  handleAssignDivision(group.id as number, {
                    isLinked: true,
                    modules: { can_compliance: value },
                    applyModulesToAll: true,
                  })
                }
              />
            )}
          </div>
        )}
      </CardHeader>

      {expanded && (
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {group.rows.map((perms) => (
            <SiteCard
              key={perms.site}
              perms={perms}
              selectedUser={selectedUser}
              isSelf={isSelf}
              isSaving={savingPermission === perms.site}
              handleToggleSiteLink={handleToggleSiteLink}
              handleToggleSitePermission={handleToggleSitePermission}
            />
          ))}
        </CardContent>
      )}
    </Card>
  );
}

function ModuleCascade({
  label, checked, disabled, onChange,
}: { label: string; checked: boolean; disabled: boolean; onChange: (value: boolean) => void }) {
  return (
    <span className="flex items-center gap-2 text-sm">
      {label}
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} />
    </span>
  );
}

interface SiteCardProps {
  perms: UserSitePermission;
  selectedUser: UserProfile;
  isSelf: (userProfile: UserProfile) => boolean;
  isSaving: boolean;
  handleToggleSiteLink: (siteId: number, isLinked: boolean) => Promise<void>;
  handleToggleSitePermission: (siteId: number, permission: string, value: boolean) => Promise<void>;
}

function SiteCard({
  perms, selectedUser, isSelf, isSaving,
  handleToggleSiteLink, handleToggleSitePermission,
}: SiteCardProps) {
  const siteId = perms.site as number;
  const complianceOn = useMenuSettings().isMenuItemVisible('onboarding');
  return (
    <Card className={`${perms.is_linked ? '' : 'opacity-60'} ${isSaving ? 'animate-pulse' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            {perms.site_name}
          </CardTitle>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {perms.is_linked ? 'Linked' : 'Not Linked'}
            </span>
            <Switch
              checked={perms.is_linked}
              onCheckedChange={(checked) => handleToggleSiteLink(siteId, checked)}
              disabled={isSelf(selectedUser) || isSaving}
            />
          </div>
        </div>
        {perms.site_code ? (
          <p className="text-xs text-muted-foreground">{perms.site_code}</p>
        ) : null}
      </CardHeader>

      {perms.is_linked ? (
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
            <span className="text-sm font-medium flex items-center gap-2">
              <Power className="h-4 w-4" />
              Active
            </span>
            <Switch
              checked={perms.is_active}
              onCheckedChange={(checked) => handleToggleSitePermission(siteId, 'is_active', checked)}
              disabled={isSelf(selectedUser) || isSaving}
            />
          </div>

          <div>
            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Modules
            </Label>
            <div className="mt-2 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm flex items-center gap-2">
                  <FileCheck className="h-4 w-4" />
                  Purchasing
                </span>
                <Switch
                  checked={perms.can_purchasing}
                  onCheckedChange={(checked) => handleToggleSitePermission(siteId, 'can_purchasing', checked)}
                  disabled={isSelf(selectedUser) || perms.is_read_only || isSaving}
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Quality
                </span>
                <Switch
                  checked={perms.can_quality}
                  onCheckedChange={(checked) => handleToggleSitePermission(siteId, 'can_quality', checked)}
                  disabled={isSelf(selectedUser) || perms.is_read_only || isSaving}
                />
              </div>
              {/* CR-8252: Supplier Onboarding (compliance) for this user at this site. */}
              {complianceOn && (
                <div className="flex items-center justify-between">
                  <span className="text-sm flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4" />
                    Compliance
                  </span>
                  <Switch
                    checked={perms.can_compliance}
                    onCheckedChange={(checked) => handleToggleSitePermission(siteId, 'can_compliance', checked)}
                    disabled={isSelf(selectedUser) || perms.is_read_only || isSaving}
                  />
                </div>
              )}
            </div>
          </div>

          {/* CR-7076 (ADMIN-MODEL): the per-site "User Role" section is gone.
              The Administrator and Approval toggles wrote UserSitePermission
              flags no backend authority check ever read — admin authority
              lives on the user-level role. Fields stay in the model, DORMANT.
              Read Only survives as the Access Override, no longer coupled to
              the dormant flags (that coupling greyed out a visible control
              because of invisible data). */}
          <div>
            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Access Override
            </Label>
            <div className="mt-2 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  Read Only
                </span>
                <Switch
                  checked={perms.is_read_only}
                  onCheckedChange={(checked) => handleToggleSitePermission(siteId, 'is_read_only', checked)}
                  disabled={isSelf(selectedUser) || isSaving}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                View everything at this site, change nothing. Overrides their user role here.
              </p>
            </div>
          </div>

          <SiteNotificationSwitches
            perms={perms}
            companyType={selectedUser.company_type}
            isSaving={isSaving}
            onToggle={(permission, value) => handleToggleSitePermission(siteId, permission, value)}
          />
        </CardContent>
      ) : null}
    </Card>
  );
}
